import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, catchError, forkJoin, map, of, switchMap, take } from 'rxjs';
import { appEnv } from '../../../app.config';
import { ResponseModel } from '../../../core/models/response.model';
import { StageModule, StageRequirement } from '../models/stage-requirement.model';

export interface StageGatePrompt {
  requirement: StageRequirement;
  module: StageModule;
  recordUuids: string[];
  fromLabel: string | null;
  toLabel: string;
  result: Subject<boolean>;
}

/**
 * Stage questions (Zoho Blueprint / HubSpot required properties): before a
 * CRM record moves into a stage, ask() looks up that move's questions and,
 * if there are any, opens the pop-up (StageGateModalComponent, mounted once
 * in the app shell). It resolves true once the answers are saved on the
 * record, false if the user cancels - the CRM services only move the card
 * on true. A bulk move to the same stage shares one pop-up: every record
 * gets the same answers.
 */
@Injectable({ providedIn: 'root' })
export class StageGateService {
  readonly prompt$ = new BehaviorSubject<StageGatePrompt | null>(null);
  private settling: Record<string, StageGatePrompt> = {};

  constructor (private http: HttpClient) {}

  ask (module: StageModule, recordUuid: string, from: string | null, to: string, fromLabel: string | null, toLabel: string): Observable<boolean> {
    if (from === to) return of(true);
    const key = `${module}|${from ?? ''}|${to}`;
    const open = this.settling[key];
    if (open) {
      open.recordUuids.push(recordUuid);
      return open.result.pipe(take(1));
    }

    const params = new URLSearchParams({ module, to, ...(from ? { from } : {}) });
    return this.http.get<ResponseModel>(`${appEnv.api}settings/stage-requirements/for-move?${params}`).pipe(
      map(res => ((res as any)?.data ?? null) as StageRequirement | null),
      // Never block a move because the lookup itself failed.
      catchError(() => of(null)),
      switchMap(requirement => {
        if (!requirement) return of(true);
        const joined = this.settling[key];
        if (joined) { joined.recordUuids.push(recordUuid); return joined.result.pipe(take(1)); }
        const prompt: StageGatePrompt = { requirement, module, recordUuids: [recordUuid], fromLabel, toLabel, result: new Subject<boolean>() };
        this.settling[key] = prompt;
        if (!this.prompt$.value) this.prompt$.next(prompt);
        return prompt.result.pipe(take(1));
      })
    );
  }

  /** Saves the answers on every record of the prompt. */
  submit (prompt: StageGatePrompt, answers: Record<string, any>): Observable<any> {
    return forkJoin(prompt.recordUuids.map(uuid => this.http.post<any>(
      `${appEnv.api}settings/stage-requirements/${prompt.requirement.id}/answers`,
      { record_uuid: uuid, answers, from_label: prompt.fromLabel, to_label: prompt.toLabel }
    )));
  }

  close (prompt: StageGatePrompt, ok: boolean): void {
    const key = Object.keys(this.settling).find(k => this.settling[k] === prompt);
    if (key) delete this.settling[key];
    prompt.result.next(ok);
    prompt.result.complete();
    const next = Object.values(this.settling)[0] ?? null;
    this.prompt$.next(next);
  }

  /* ---- settings (Workflow > Pipelines) ---- */

  list (module: StageModule): Observable<StageRequirement[]> {
    return this.http.get<ResponseModel>(`${appEnv.api}settings/stage-requirements?module=${module}`).pipe(map(res => (res as any).data ?? []));
  }

  save (r: StageRequirement): Observable<StageRequirement> {
    const body = { module: r.module, stage_key: r.stageKey, from_stages: r.fromStages, title: r.title, description: r.description, items: r.items, is_active: r.isActive };
    const call = r.id
      ? this.http.put<ResponseModel>(`${appEnv.api}settings/stage-requirements/${r.id}`, body)
      : this.http.post<ResponseModel>(`${appEnv.api}settings/stage-requirements`, body);
    return call.pipe(map(res => (res as any).data));
  }

  remove (id: number): Observable<boolean> {
    return this.http.delete<ResponseModel>(`${appEnv.api}settings/stage-requirements/${id}`).pipe(map(res => !!(res as any)?.success));
  }
}
